/**
 * Tooltip.tsx - Universal tooltip component for rollover information
 * 
 * CHANGE HISTORY:
 * - 251105_1915 danielbeer: Added hover detection to keep tooltip visible
 * - 251101_0756 danielbeer: Initial creation for graph rollover functionality
 * 
 * Provides contextual information on hover with smart positioning
 */

import React, { useEffect, useRef, useState } from 'react';
import './Tooltip.css';

export interface TooltipProps {
  isVisible: boolean;
  position: { x: number; y: number };
  content: React.ReactNode | string;
  title?: string;
  delay?: number;
  offset?: { x: number; y: number };
  maxWidth?: number;
  className?: string;
  onMouseEnter?: () => void;  // New: Called when mouse enters tooltip
  onMouseLeave?: () => void;  // New: Called when mouse leaves tooltip
}

export const Tooltip: React.FC<TooltipProps> = ({
  isVisible,
  position,
  content,
  title,
  delay = 300,
  offset = { x: 10, y: 10 },
  maxWidth = 300,
  className = '',
  onMouseEnter,
  onMouseLeave,
}) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [show, setShow] = useState(false);
  const [adjustedPosition, setAdjustedPosition] = useState(position);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Handle delay before showing
  useEffect(() => {
    if (isVisible) {
      timeoutRef.current = setTimeout(() => {
        setShow(true);
      }, delay);
    } else {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      setShow(false);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isVisible, delay]);

  // Adjust position to keep tooltip on screen
  useEffect(() => {
    if (!show || !tooltipRef.current) return;

    const tooltip = tooltipRef.current;
    const rect = tooltip.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let x = position.x + offset.x;
    let y = position.y + offset.y;

    // Adjust horizontal position
    if (x + rect.width > viewportWidth) {
      x = position.x - rect.width - offset.x;
    }

    // Adjust vertical position
    if (y + rect.height > viewportHeight) {
      y = position.y - rect.height - offset.y;
    }

    // Ensure tooltip doesn't go off left edge
    if (x < 0) {
      x = offset.x;
    }

    // Ensure tooltip doesn't go off top edge
    if (y < 0) {
      y = offset.y;
    }

    // Only update if position actually changed (prevents infinite loop)
    setAdjustedPosition(prev => {
      if (prev.x === x && prev.y === y) return prev;
      return { x, y };
    });
  }, [show, position, offset]);

  if (!show) return null;

  return (
    <div
      ref={tooltipRef}
      className={`viewr-tooltip ${className}`}
      style={{
        left: adjustedPosition.x,
        top: adjustedPosition.y,
        maxWidth,
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {title && <div className="viewr-tooltip-title">{title}</div>}
      <div className="viewr-tooltip-content">{content}</div>
    </div>
  );
};

export default Tooltip;
